<template>
  <div class="login">
    <div class="login__language">
      <language-control></language-control>
    </div>
    <div class="login__title">
      {{ $t('views.Auth.Login.loginTitle') }}
    </div>
    <div class="login__inner">
      <div class="login__content">
        <form @submit.prevent="recaptchaExec()">
          <v-input
            class="mb-1"
            :class="errorInputClass"
            :error="$t(credentialsErrors.slug)"
            :placeholder="$t('components.ManagersList.newManager.login')"
            v-model="credentials.slug"
          ></v-input>
          <v-input
            class="mb-2"
            :class="errorInputClass"
            :error="$t(credentialsErrors.password)"
            :placeholder="$t('components.ManagersList.newManager.password')"
            :type-name="typePassword ? 'password' : null"
            v-model="credentials.password"
            icon-type="button"
            icon-position="right"
            :icon="typePassword ? 'icon-eye' : 'icon-crossed-out-eye'"
            @iconClick="typePassword = !typePassword"
          ></v-input>

          <vue-recaptcha
            ref="recaptcha"
            size="invisible"
            sitekey="6Lcll9oZAAAAAHnAmSi3s99CN6dvyITQODxou4X5"
            loadRecaptchaScript
            @verify="checkVerify"
            @error="errorCaptcha"
          ></vue-recaptcha>
          <v-button action-type="submit" width="full" :loading="loading">{{ $t('views.Auth.Login.continue') }}</v-button>
        </form>
      </div>
    </div>

    <div class="login__register text--medium text--uppercase text--small">
      <v-modal
        v-model="showSignup"
        animation="slide-up"
        position="center"
        :full="!screen.breakPoint.tabPort"
        :no-background="!screen.breakPoint.tabPort"
        handle-click-bg
      >
        <template v-slot:activator="{ on }">
          <a href="#" @click.prevent="on">{{ $t('views.Auth.Login.signUp') }}</a>
        </template>

        <template v-slot:header>
          {{ $t('views.Auth.Login.joinUs') }}
        </template>

        <div
          style="max-width: 52rem"
          class="mx-auto"
        >
          <form @submit.prevent="recaptchaExecSignUp()">
            <v-input :error="$t(signUpDataErrors.name)" v-model="signUpData.name" :placeholder="$t('views.Auth.Login.name')" class="mb-1 mt-2"></v-input>
            <v-input :error="$t(signUpDataErrors.email)" v-model="signUpData.email" :placeholder="$t('views.Auth.Login.email')" class="mb-1"></v-input>
            <v-input :error="$t(signUpDataErrors.phone)" type-name="tel" v-model="signUpData.phone" :placeholder="$t('views.Auth.Login.phone')" class="mb-1"></v-input>
            <v-input :error="$t(signUpDataErrors.login)" v-model="signUpData.login" :placeholder="$t('components.ManagersList.newManager.login')" class="mb-1"></v-input>
            <v-input :error="$t(signUpDataErrors.password)" v-model="signUpData.password" :placeholder="$t('views.Auth.Login.password')" class="mb-3" type-name="password"></v-input>
          </form>
        </div>

        <template #footer>
          <div
            style="max-width: 52rem"
            class="mx-auto"
          >
            <form @submit.prevent="recaptchaExecSignUp()">
              <vue-recaptcha
                ref="recaptchaSignUp"
                size="invisible"
                sitekey="6Lcll9oZAAAAAHnAmSi3s99CN6dvyITQODxou4X5"
                loadRecaptchaScript
                @verify="checkVerifySignUp"
              ></vue-recaptcha>
              <v-button
                width="full"
                class="mb-2"
                action-type="submit"
                :loading="loadingSignUp"
              >{{ $t('views.Auth.Login.createAccount') }}</v-button>
            </form>
          </div>
        </template>
      </v-modal>
    </div>

    <div class="login__register text--medium text--uppercase text--small">
      <v-modal v-model="showRestoreModal" no-background animation="slide-up"  position="center" full no-padding>
        <template v-slot:header>
          {{ $t('views.Auth.Login.restorePass') }}
        </template>
          <template v-slot:activator="{ on }">
            <a href="#" @click.prevent="on">{{ $t('views.Auth.Login.forgotPass') }}</a>
          </template>
        <restore></restore>
      </v-modal>
    </div>

    <v-modal v-model="showPinCodeModal" no-background animation="slide-up" unclosable position="center" full no-padding>
      <pin-code @reset="showPinCodeModal = false" :type="hasPinToken ? 'input' : 'set'" :credentials="credentials"></pin-code>
    </v-modal>

    <transition name="slow-fade">
      <div class="login__preview" v-show="showPreview">
        <div class="login__logo-container">
          <svg class="login__logo">
            <use xlink:href="#icon-logo-planet"></use>
          </svg>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { Capacitor } from '@capacitor/core'

import vInput from '@/ui/controls/vInput'
import vButton from '@/ui/controls/vButton'
import vModal from '@/ui/modals/vModal'

import LanguageControl from '@/ui/controls/LanguageControl'
import PinCode from '@/components/PinCode'
import Restore from '@/components/Restore'

import screen from '@/mixins/screen'
import { notifyGraphqlError, refreshSession } from '@/mixins/handleGraphqlError'

import { hasApolloToken, onLogin } from '@/vue-apollo'

import { LoginGQL, SignUpGQL } from '@/graphql/mutations'
import { hasPinToken } from '@/mixins/pinToken'

export default {
  components: {
    Restore,
    vInput,
    vButton,
    vModal,
    LanguageControl,
    PinCode,

    VueRecaptcha
  },

  data () {
    return {
      showRestoreModal: this.$route.query.login && this.$route.query.code,
      showSignup: this.$route.query.mode === 'signup' || false,

      loading: false,
      loadingSignUp: false,

      credentials: {
        slug: null,
        password: null,
        reCaptcha: null
      },

      credentialsErrors: {
        slug: null,
        password: null
      },

      signUpData: {
        name: null,
        phone: null,
        email: null,
        login: null,
        password: null,
        reCaptcha: null
      },

      signUpDataErrors: {
        name: null,
        phone: null,
        email: null,
        login: null,
        password: null
      },

      showPreview: !Capacitor.isNativePlatform(),
      verified: false,
      errorInputClass: null,

      typePassword: true,

      showPinCodeModal: false
    }
  },

  mixins: [screen, notifyGraphqlError, hasPinToken, refreshSession],

  computed: {
    disableLoginButton () {
      let fieldsNum = 0
      let filled = 0
      for (const key of Object.keys(this.credentials)) {
        if (key !== 'reCaptcha') {
          fieldsNum++
          if (this.credentials[key] && this.credentials[key].length) {
            filled++
          }
        }
      }

      return filled !== fieldsNum
    },

    disableSignupButton () {
      let fieldsNum = 0
      let filled = 0
      for (const key of Object.keys(this.signUpData)) {
        if (key !== 'reCaptcha') {
          fieldsNum++
          if (this.signUpData[key] && this.signUpData[key].length) {
            filled++
          }
        }
      }

      return filled !== fieldsNum
    }
  },

  methods: {
    errorCaptcha () {
      this.$notify({
        type: 'error',
        message: this.$i18n.t('errors.captchaError'),
        delay: 150,
        timeout: 3000
      })
    },

    checkVerify (e) {
      if (!this.validateCredentials()) {
        this.recaptchaReset()
        return
      }
      this.credentials.reCaptcha = e
      this.login()
    },

    validateCredentials () {
      const credentials = this.credentials

      for (const key of Object.keys(credentials)) {
        if ((!credentials[key] || credentials[key] === '') && key !== 'reCaptcha') {
          this.credentialsErrors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        }
      }

      return !(Object.keys(this.credentialsErrors).some(key => this.credentialsErrors[key] && true))
    },

    validateSignUpData () {
      const signUpData = this.signUpData

      for (const key of Object.keys(signUpData)) {
        if ((!signUpData[key] || signUpData[key] === '') && key !== 'reCaptcha') {
          this.signUpDataErrors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        }

        if (key === 'password' && signUpData[key].length < 6) {
          this.signUpDataErrors[key] = 'errors.code.min6'
        }
        if (key === 'password' && signUpData[key].length > 200) {
          this.signUpDataErrors[key] = 'errors.code.max200'
        }
      }

      return !(Object.keys(this.signUpDataErrors).some(key => this.signUpDataErrors[key] && true))
    },

    checkVerifySignUp (e) {
      if (!this.validateSignUpData()) {
        this.recaptchaSignUpReset()
        return
      }
      this.signUpData.reCaptcha = e
      this.signUp()
    },

    recaptchaExec () {
      this.$refs.recaptcha.execute()
    },

    recaptchaExecSignUp () {
      this.$refs.recaptchaSignUp.execute()
    },

    async login () {
      if (!this.credentials.reCaptcha) {
        return
      }
      this.loading = true
      // eslint-disable-next-line no-undef,no-labels
      // this.credentials.reCaptcha = grecaptcha.getResponse()

      try {
        const { data } = await this.$apollo.mutate({
          mutation: LoginGQL,
          variables: {
            credentials: this.credentials
          }
        })

        await onLogin(this.$apollo.getClient(), data.login.accessToken, data.login.refreshToken)

        this.refreshSession()
        if (Capacitor.isNativePlatform() && !this.hasPinToken) {
          this.showPinCodeModal = true
        } else {
          await this.$router.push({
            name: 'dashboard'
          })
        }

        this.loading = false
        this.recaptchaReset()
      } catch (e) {
        this.loading = false
        const errors = e.graphQLErrors

        if (!Array.isArray(errors)) {
          this.notifyGraphqlError(e)
        } else {
          for (const err of errors) {
            const code = err.message
            if (code === 'Unauthorized') {
              this.$notify({
                type: 'error',
                message: this.$i18n.t('errors.codes.loginOrPasswordIncorrect'),
                delay: 150,
                timeout: 3000
              })
            } else {
              this.notifyGraphqlError(e)
            }
          }
        }
        this.recaptchaReset()

        setTimeout(() => {
          this.errorInputClass = 'shake'
        }, 100)
        setTimeout(() => {
          this.errorInputClass = null
        }, 1300)
      }
    },

    async signUp () {
      if (!this.signUpData.reCaptcha) {
        return
      }
      this.loadingSignUp = true
      // eslint-disable-next-line no-undef,no-labels
      // this.credentials.reCaptcha = grecaptcha.getResponse()

      try {
        const { data } = await this.$apollo.mutate({
          mutation: SignUpGQL,
          variables: {
            ...this.signUpData
          }
        })

        await onLogin(this.$apollo.getClient(), data.signUp.accessToken, data.signUp.refreshToken)
        this.recaptchaSignUpReset()
        this.showSignup = false
        this.refreshSession()

        setTimeout(async () => {
          if (Capacitor.isNativePlatform() && !this.hasPinToken) {
            this.showPinCodeModal = true
          } else {
            await this.$router.push({
              name: 'dashboard'
            })
          }
        }, 500)
      } catch (e) {
        this.loadingSignUp = false
        const errors = e.graphQLErrors

        this.recaptchaSignUpReset()

        for (const err of errors) {
          const code = err.message
          if (code === 'Unauthorized') {
            this.$notify({
              type: 'error',
              message: this.$i18n.t('errors.codes.loginOrPasswordIncorrect'),
              delay: 150,
              timeout: 3000
            })
          } else if (code === 'userEmailExists') {
            this.$notify({
              type: 'error',
              message: this.$i18n.t('errors.codes.userEmailExists'),
              delay: 150,
              timeout: 3000
            })
          } else {
            this.notifyGraphqlError(e)
          }
        }
      }
    },

    verifyCaptcha (res) {
      this.$emit('input', res)
    },

    recaptchaReset () {
      this.credentials.reCaptcha = null
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset()
      }
    },

    recaptchaSignUpReset () {
      this.signUpData.reCaptcha = null
      this.$refs.recaptchaSignUp.reset()
    }
  },

  watch: {
    showSignup (val) {
      if (val) {
        this.$router.push({ query: { mode: 'signup' } })
      } else {
        this.$router.push({ query: null })
      }
    },

    credentials: {
      handler: function () {
        this.credentialsErrors = {
          slug: null,
          password: null
        }
      },
      deep: true
    },

    signUpData: {
      handler: function () {
        this.signUpDataErrors = {
          name: null,
          phone: null,
          email: null,
          login: null,
          password: null
        }
      },
      deep: true
    }
  },

  async mounted () {
    const authorized = await hasApolloToken()
    if (authorized) {
      await this.$router.push({
        name: 'dashboard'
      })

      return
    }

    if (this.hasPinToken) {
      this.showPinCodeModal = true
    }
    setTimeout(() => {
      this.showPreview = false
    }, 1000)
  }
}
</script>

<style lang="scss">
.login {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 28rem;
  position: relative;

  &__language {
    position: absolute;
    z-index: 1;
    top: $default-spacing-mobile;
    right: $default-spacing-mobile;

    @include respond(tab-port) {
      top: $default-spacing;
      right: $default-spacing;
    }

    @include iosTop($default-spacing-mobile);
  }

  @include computedVh(100);

  @include respond(phone-x-large) {
    justify-content: center;
  }

  &__title {
    margin-top: calc(5rem + env(safe-area-inset-top));
    margin-bottom: 2rem;
    font-weight: 200;
    font-size: 2rem;
    order: 1;

    @include respond(phone-medium) {
      margin-bottom: 4rem;
    }
  }

  &__inner {
    padding: 2rem;
    margin-left: $default-spacing-mobile;
    margin-right: $default-spacing-mobile;
    align-self: center;
    max-width: 50rem;

    background-color: rgba($color-azure-light, .4);
    border-radius: $border-radius-large;
    text-align: center;
    order: 2;
    /*padding: 3rem 5rem;*/

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    @include respond(phone-medium) {
      /*padding: 5rem 5rem;*/
    }

    @include respond(phone-x-large) {
      border-radius: $border-radius-large;
    }
  }

  &__content {
    width: 100%;
    //width: 70%;
  }

  &__logo-container {
    //margin-bottom: 2rem;
    //margin-top: auto;
    //order: 4;
  }

  &__logo {
    margin-top: 2rem;
    width: 20rem;
    height: 3.5rem;
  }

  &__register {
    margin-top: 3rem;
    margin-bottom: 2rem;
    order: 3;
  }

  &__forget{
    margin-bottom: 2rem;
    order: 3;
  }

  &__preview {
    position: absolute;
    background-color: $color-white;
    width: 100%;
    height: 100%;
    z-index: 1;

    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.grecaptcha-badge {
  display: none;
}
</style>
